/* =======================================================
   ✅ OGATU — MEDIA BANNER (ASSET JS) — FULL REPLACEMENT
   File: assets/section-ogatu-stack-media-banner.js

   Mode 1 (stacked): no JS needed
   Mode 2 (split): desktop sticky + vertical scroll drives translateX
   Mode 3 (reveal): CSS-only sticky reveal (no JS)

   Video:
   - Optional mute/unmute button per block
   ======================================================= */

(function () {
  const SELECTOR = '[data-ogatu-mbanner]';
  const clamp = (n, a, b) => Math.min(b, Math.max(a, n));

  function isDesktop() {
    return window.matchMedia('(min-width: 750px)').matches;
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function getMultiplier(intensity) {
    switch ((intensity || 'med').toLowerCase()) {
      case 'low': return 1.35;
      case 'high': return 0.85;
      default: return 1.00;
    }
  }

  function setupMuteButtons(root) {
    const buttons = root.querySelectorAll('[data-ogatu-mute]');
    buttons.forEach((btn) => {
      if (btn.__ogatuBound) return;
      btn.__ogatuBound = true;

      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        const wrap = btn.closest('.ogatu-mbanner__media');
        const video = wrap ? wrap.querySelector('video') : null;
        if (!video) return;

        const willUnmute = video.muted === true;
        video.muted = !willUnmute;

        if (willUnmute) {
          const p = video.play?.();
          if (p && typeof p.catch === 'function') p.catch(() => {});
        }

        btn.setAttribute('aria-pressed', willUnmute ? 'true' : 'false');
        btn.setAttribute('aria-label', willUnmute ? 'Mute video' : 'Unmute video');
      });
    });
  }

  class OgatuMediaBanner {
    constructor(root) {
      this.root = root;
      this.viewport = root.querySelector('[data-viewport]');
      this.track = root.querySelector('[data-track]');

      this.mode = root.dataset.mode || 'stacked';
      this.stickyEnabled = root.dataset.splitSticky === 'true';
      this.intensity = root.dataset.intensity || 'med';
      this.startDelay = parseInt(root.dataset.startDelay || '120', 10) || 0;

      this.raf = null;
      this.active = false;

      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);

      setupMuteButtons(root);
      this.mount();
    }

    shouldRun() {
      return (
        this.mode === 'split' &&
        this.stickyEnabled &&
        isDesktop() &&
        !prefersReducedMotion() &&
        this.viewport &&
        this.track
      );
    }

    mount() {
      if (!this.shouldRun()) {
        this.teardown();
        return;
      }

      this.active = true;
      this.recalc();

      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize);

      this.onScroll();
    }

    teardown() {
      this.active = false;
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);

      if (this.raf) cancelAnimationFrame(this.raf);
      this.raf = null;

      if (this.track) this.track.style.transform = 'translate3d(0,0,0)';
      this.root.style.removeProperty('--ogatu-mbanner-scroll-len');
    }

    recalc() {
      if (!this.active) return;

      const vw = this.viewport.clientWidth;
      const trackW = this.track.scrollWidth;

      this.travel = Math.max(0, trackW - vw);

      const mult = getMultiplier(this.intensity);
      this.scrollLen = Math.max(1, Math.round((this.travel * mult) + this.startDelay));

      this.root.style.setProperty('--ogatu-mbanner-scroll-len', `${this.scrollLen}px`);
      this.rootTop = this.root.getBoundingClientRect().top + window.scrollY;
    }

    onResize() {
      this.mode = this.root.dataset.mode || 'stacked';
      this.stickyEnabled = this.root.dataset.splitSticky === 'true';
      this.intensity = this.root.dataset.intensity || 'med';
      this.startDelay = parseInt(this.root.dataset.startDelay || '120', 10) || 0;

      if (!this.shouldRun()) {
        this.teardown();
        return;
      }

      if (!this.active) {
        this.mount();
        return;
      }

      this.recalc();
      this.onScroll();
    }

    onScroll() {
      if (!this.active) return;
      if (this.raf) return;

      this.raf = requestAnimationFrame(() => {
        this.raf = null;

        const y = window.scrollY;
        const local = y - this.rootTop;

        const effective = Math.max(0, local - this.startDelay);
        const denom = Math.max(1, this.scrollLen - this.startDelay);
        const progress = clamp(effective / denom, 0, 1);

        const x = -Math.round(this.travel * progress);
        this.track.style.transform = `translate3d(${x}px, 0, 0)`;
      });
    }
  }

  function mountAll() {
    document.querySelectorAll(SELECTOR).forEach((root) => {
      if (root.__ogatuMediaBanner) return;
      root.__ogatuMediaBanner = new OgatuMediaBanner(root);
    });
  }

  function remountOnSectionEvents(e) {
    const root = e.target?.querySelector?.(SELECTOR);
    if (!root) return;

    if (root.__ogatuMediaBanner) {
      root.__ogatuMediaBanner.teardown();
      delete root.__ogatuMediaBanner;
    }
    root.__ogatuMediaBanner = new OgatuMediaBanner(root);
  }

  document.addEventListener('DOMContentLoaded', mountAll);
  document.addEventListener('shopify:section:load', remountOnSectionEvents);
  document.addEventListener('shopify:section:unload', (e) => {
    const root = e.target?.querySelector?.(SELECTOR);
    if (root?.__ogatuMediaBanner) {
      root.__ogatuMediaBanner.teardown();
      delete root.__ogatuMediaBanner;
    }
  });
})();